import {
  Badge,
  Divider,
  Drawer,
  Grid,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import React from "react";
import { Line } from "react-chartjs-2";
import {
  PerformanceCustomerSummary,
  PerformanceDelayPeriodPoint,
  PerformanceLifecyclePeriodPoint,
  PerformancePostAnalytics,
  PerformanceThroughputPeriodPoint,
  WorkflowStatusKey,
} from "../../types/performance_analytics";
import {
  formatBoolean,
  formatCount,
  formatDateTime,
  formatDuration,
  formatPercentage,
  getStatusColor,
  getStatusLabel,
  SectionPanel,
  STATUS_COLORS,
  STATUS_LABELS,
  UNAVAILABLE_LABEL,
} from "../../pages/performance_analytics/shared";
import AnalyticsMetricCard from "./metric_card";
import PerformancePostsTable from "./posts_table";

const STATUS_KEYS = Object.keys(STATUS_LABELS) as WorkflowStatusKey[];

const buildSeriesOptions = (title: string, color: string) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: { display: false, text: title },
  },
  scales: {
    y: { ticks: { callback: (value: any) => `${value}` } },
  },
  elements: {
    line: { tension: 0.3 },
  },
  color,
});

const PostDetailPanel = ({ post }: { post: PerformancePostAnalytics }) => {
  return (
    <Stack spacing="md">
      <Title order={5}>Detalhe do post</Title>
      <Grid>
        <Grid.Col md={4}>
          <AnalyticsMetricCard label="Ciclo total" value={formatDuration(post.totalLifecycleTimeMs, "detailed")} />
        </Grid.Col>
        <Grid.Col md={4}>
          <AnalyticsMetricCard label="Tempo de aprovação" value={formatDuration(post.approvalTimeMs, "detailed")} />
        </Grid.Col>
        <Grid.Col md={4}>
          <AnalyticsMetricCard label="Gargalo atual" value={formatDuration(post.currentBottleneckDurationMs, "detailed")} />
        </Grid.Col>
      </Grid>

      <Paper withBorder p="md">
        <Stack spacing="sm">
          <Title order={6}>Duração por status</Title>
          <Table verticalSpacing="xs">
            <thead>
              <tr>
                <th>Status</th>
                <th>Duração</th>
              </tr>
            </thead>
            <tbody>
              {STATUS_KEYS.map((statusKey) => (
                <tr key={statusKey}>
                  <td>{STATUS_LABELS[statusKey]}</td>
                  <td>{formatDuration(post.timeInStatusMs?.[statusKey], "detailed")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack spacing="sm">
          <Title order={6}>Timeline do fluxo</Title>
          <Table verticalSpacing="xs">
            <thead>
              <tr>
                <th>Status</th>
                <th>Status bruto</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {(post.statusHistory || []).map((entry, index) => (
                <tr key={`${entry.createdAt}-${index}`}>
                  <td>
                    <Badge color={getStatusColor(entry.statusKey)} variant="light">
                      {getStatusLabel(entry.statusKey)}
                    </Badge>
                  </td>
                  <td>{entry.rawStatusKey || UNAVAILABLE_LABEL}</td>
                  <td>{formatDateTime(entry.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack spacing="sm">
          <Title order={6}>Destinos de publicação</Title>
          <Table verticalSpacing="xs">
            <thead>
              <tr>
                <th>Origem</th>
                <th>Agendado em</th>
                <th>Publicado em</th>
              </tr>
            </thead>
            <tbody>
              {(post.publicationDestinations || []).map((destination, index) => (
                <tr key={`${destination.origin || "origin"}-${index}`}>
                  <td>{destination.origin || UNAVAILABLE_LABEL}</td>
                  <td>{formatDateTime(destination.scheduledAt)}</td>
                  <td>{formatDateTime(destination.publishedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default function ClientDetailDrawer({
  opened,
  onClose,
  customer,
  posts,
  lifecycleSeries,
  delaySeries,
  throughputSeries,
  selectedJobId,
  onSelectPost,
}: {
  opened: boolean;
  onClose: () => void;
  customer: PerformanceCustomerSummary | null;
  posts: PerformancePostAnalytics[];
  lifecycleSeries: PerformanceLifecyclePeriodPoint[];
  delaySeries: PerformanceDelayPeriodPoint[];
  throughputSeries: PerformanceThroughputPeriodPoint[];
  selectedJobId?: string | null;
  onSelectPost: (post: PerformancePostAnalytics) => void;
}) {
  const selectedPost = React.useMemo(
    () => posts.find((post) => post.jobId === selectedJobId) || null,
    [posts, selectedJobId]
  );

  const lifecycleChartData = React.useMemo(() => ({
    labels: lifecycleSeries.map((item) => item.label),
    datasets: [
      {
        label: "Lifecycle médio",
        data: lifecycleSeries.map((item) => item.customers.find((entry) => entry.customerId === customer?.customerId)?.averageLifecycleTimeMs ?? null),
        borderColor: "#2563eb",
        backgroundColor: "rgba(37,99,235,0.2)",
      },
    ],
  }), [customer?.customerId, lifecycleSeries]);

  const delayChartData = React.useMemo(() => ({
    labels: delaySeries.map((item) => item.label),
    datasets: [
      {
        label: "Atraso médio",
        data: delaySeries.map((item) => item.customers.find((entry) => entry.customerId === customer?.customerId)?.averageDelayMs ?? null),
        borderColor: "#dc2626",
        backgroundColor: "rgba(220,38,38,0.18)",
      },
    ],
  }), [customer?.customerId, delaySeries]);

  const throughputChartData = React.useMemo(() => ({
    labels: throughputSeries.map((item) => item.label),
    datasets: [
      {
        label: "Posts concluídos",
        data: throughputSeries.map((item) => item.customers.find((entry) => entry.customerId === customer?.customerId)?.completedPosts ?? null),
        borderColor: "#16a34a",
        backgroundColor: "rgba(22,163,74,0.18)",
      },
    ],
  }), [customer?.customerId, throughputSeries]);

  if (!customer) {
    return (
      <Drawer opened={opened} onClose={onClose} title="Detalhe do cliente" position="right" size="xl">
        <Text c="dimmed">Cliente não encontrado para o contexto atual.</Text>
      </Drawer>
    );
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={customer.customerName}
      position="right"
      size="90%"
      padding="md"
    >
      <Stack spacing="lg">
        <Grid>
          <Grid.Col md={3}>
            <AnalyticsMetricCard label="Posts criados" value={formatCount(customer.volume.totalPostsCreated)} />
          </Grid.Col>
          <Grid.Col md={3}>
            <AnalyticsMetricCard label="Posts publicados" value={formatCount(customer.volume.totalPostsPublished)} />
          </Grid.Col>
          <Grid.Col md={3}>
            <AnalyticsMetricCard label="Lifecycle médio" value={formatDuration(customer.lifecycle.averageLifecycleTimeMs)} />
          </Grid.Col>
          <Grid.Col md={3}>
            <AnalyticsMetricCard label="Taxa de retrabalho" value={formatPercentage(customer.rework.reworkRate)} />
          </Grid.Col>
        </Grid>

        <SectionPanel title="Resumo operacional" description="Principais indicadores do cliente selecionado.">
          <Grid>
            <Grid.Col md={4}>
              <AnalyticsMetricCard label="Tempo médio de aprovação" value={formatDuration(customer.approvalTime.averageApprovalTimeMs)} />
            </Grid.Col>
            <Grid.Col md={4}>
              <AnalyticsMetricCard label="Aprovado sem ajustes" value={formatPercentage(customer.approvalFlow.approvedWithoutChangesRate)} />
            </Grid.Col>
            <Grid.Col md={4}>
              <AnalyticsMetricCard label="Posts atrasados" value={formatCount(customer.delay.delayedPostsCount)} hint={formatPercentage(customer.delay.delayedPostsRate)} />
            </Grid.Col>
          </Grid>
        </SectionPanel>

        <SectionPanel title="Tendências do cliente" description="Snippets com lifecycle, atraso e throughput ao longo do período.">
          <Grid>
            <Grid.Col md={4}>
              <div style={{ height: 220 }}>
                <Line data={lifecycleChartData} options={buildSeriesOptions("Lifecycle", "#2563eb")} />
              </div>
            </Grid.Col>
            <Grid.Col md={4}>
              <div style={{ height: 220 }}>
                <Line data={delayChartData} options={buildSeriesOptions("Atraso", "#dc2626")} />
              </div>
            </Grid.Col>
            <Grid.Col md={4}>
              <div style={{ height: 220 }}>
                <Line data={throughputChartData} options={buildSeriesOptions("Throughput", "#16a34a")} />
              </div>
            </Grid.Col>
          </Grid>
        </SectionPanel>

        <SectionPanel title="Gargalos e aprovação" description="Métricas por etapa do fluxo para leitura rápida.">
          <Grid>
            {STATUS_KEYS.map((statusKey) => (
              <Grid.Col md={4} key={statusKey}>
                <Paper withBorder p="md" h="100%">
                  <Stack spacing={4}>
                    <Group position="apart">
                      <Text fw={600}>{STATUS_LABELS[statusKey]}</Text>
                      <Badge color={STATUS_COLORS[statusKey]} variant="light">
                        {formatCount(customer.timeInStatus.bottlenecks?.[statusKey] ?? 0)}
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Tempo médio no status
                    </Text>
                    <Text fw={700}>{formatDuration(customer.timeInStatus.averageTimeMsByStatus?.[statusKey])}</Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        </SectionPanel>

        <Divider />

        <SectionPanel title="Posts recentes" description="Clique em um item para abrir o drilldown completo.">
          <PerformancePostsTable
            posts={posts}
            onSelectPost={onSelectPost}
            selectedJobId={selectedJobId}
            showCustomer={false}
          />
        </SectionPanel>

        {selectedPost ? <PostDetailPanel post={selectedPost} /> : null}
      </Stack>
    </Drawer>
  );
}